import classNames from 'classnames';
import React, { useState } from 'react';
import styles from 'components/share-video/transcript/transcript-header/styles.module.less';
import {
  Arrange,
  Button,
  Container,
  IconButton,
  Spacer,
  SvgSearch,
  SvgUndo,
  Text,
  TextButton,
  Tooltip,
} from '@loomhq/lens';
import {
  CORRECT_TRANSCRIPT_TITLE,
  UNDO_ALL_CORRECTIONS_TITLE,
  SEARCH_TRANSCRIPT_TITLE,
} from 'components/share-video/transcript/constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  toggleIsTranscriptSearchOpen,
  updateTranscriptPanelMode,
} from 'actions/shareVideo';
import { TRANSCRIPT_PANEL_MODE_VIEW } from 'reducers/shareVideo';
import {
  revertAllCorrections,
  TranscriptionState,
} from 'reducers/transcription';
import { selectCurrentVideoId } from 'selectors/videoPlayer';
import { selectTranscriptCorrectionsEnabled } from 'selectors/transcripts';
import { useLayer } from 'react-laag';
import * as analytics from 'utilities/analytics';
import { TRANSCRIPTION_REVERT_ALL_CLICKED } from 'constants/events';

const TranscriptHeaderCorrections = ({
  iconSize,
}: {
  iconSize: 'small' | 'medium';
}): JSX.Element => {
  const dispatch = useDispatch();
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  // we omit the content to hide the tooltip when the popup is open, to avoid overlap
  const undoTooltipContent = confirmationVisible
    ? ''
    : UNDO_ALL_CORRECTIONS_TITLE;
  const videoId = useSelector<TranscriptionState, string>(selectCurrentVideoId);
  const correctionsEnabled = useSelector<TranscriptionState, boolean>(
    selectTranscriptCorrectionsEnabled
  );

  const hideConfirmation = () => {
    setConfirmationVisible(false);
  };

  const { triggerProps, layerProps, renderLayer } = useLayer({
    isOpen: confirmationVisible,
    placement: 'bottom-center',
    triggerOffset: 4,
    auto: true,
    onOutsideClick: hideConfirmation,
  });

  const adjustedLayerProps = {
    ...layerProps,
    style: { ...layerProps.style, zIndex: 3 },
  };

  const handleSearchClick = () => {
    dispatch(toggleIsTranscriptSearchOpen(true));
  };

  const handleDoneClick = () => {
    dispatch(updateTranscriptPanelMode(TRANSCRIPT_PANEL_MODE_VIEW));
  };

  const handleRevertAllClick = () => {
    analytics.track(TRANSCRIPTION_REVERT_ALL_CLICKED, {
      video_id: videoId,
    });
    setConfirmationVisible(true);
  };

  const handleConfirmationClicked = () => {
    hideConfirmation();
    dispatch(revertAllCorrections(videoId));
  };

  return (
    <>
      <div
        className={classNames(
          styles.header,
          styles.headerCorrections,
          styles.headerFirstLine
        )}
      >
        <div className="flex grow:1">
          <Text size="medium" fontWeight="bold">
            {CORRECT_TRANSCRIPT_TITLE}
          </Text>
        </div>
        <div className="flex items:center justify:flexEnd ">
          <Spacer left={1}>
            <div {...triggerProps}>
              <Tooltip content={undoTooltipContent} placement="bottomCenter">
                <IconButton
                  size={iconSize}
                  altText={UNDO_ALL_CORRECTIONS_TITLE}
                  icon={<SvgUndo />}
                  onClick={handleRevertAllClick}
                  isDisabled={!correctionsEnabled}
                />
              </Tooltip>
            </div>
          </Spacer>
          <Spacer left={1}>
            <Tooltip content={SEARCH_TRANSCRIPT_TITLE} placement="bottomCenter">
              <IconButton
                size={iconSize}
                altText={SEARCH_TRANSCRIPT_TITLE}
                icon={<SvgSearch />}
                onClick={handleSearchClick}
              />
            </Tooltip>
          </Spacer>
          <Spacer left={1}>
            <TextButton onClick={handleDoneClick}>Done</TextButton>
          </Spacer>
        </div>
      </div>
      {confirmationVisible &&
        renderLayer(
          <div className="tooltip" {...adjustedLayerProps}>
            <Container
              position="relative"
              width="max-content"
              height="max-content"
              backgroundColor="overlay"
              borderColor="border"
              padding="12px"
              borderSide="all"
              radius="medium"
              borderWidth="1px"
              shadow="medium"
            >
              <Text>
                Are you sure you want to undo all <br />
                corrections made to the transcript?
              </Text>
              <Spacer bottom="medium" />
              <Arrange gap={1} justifyContent="end">
                <Button
                  variant="neutral"
                  size="small"
                  onClick={hideConfirmation}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleConfirmationClicked}
                >
                  Yes
                </Button>
              </Arrange>
            </Container>
          </div>
        )}
    </>
  );
};

export default TranscriptHeaderCorrections;
